import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { combineLatest, distinctUntilChanged } from 'rxjs';

import { convertNumber } from '../../../../../shared/all.helpers';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import { GenericComponent } from '../generic/generic.component';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexYAxis,
    ApexGrid,
    ApexTooltip,
    ApexPlotOptions,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec7RedLight,
    colorAec8RedDark,
} from 'src/app/shared/all.constants';
import {
    CONTINUITY_QUALITY_SUPPLY_INCIDENT_COMPARISON_CHART,
    CONTINUITY_QUALITY_SUPPLY_LV_INCIDENT_CHART,
    CONTINUITY_QUALITY_SUPPLY_MV_INCIDENT_CHART,
} from '../../assets/keys';

const KEYS = [
    CONTINUITY_QUALITY_SUPPLY_MV_INCIDENT_CHART,
    CONTINUITY_QUALITY_SUPPLY_LV_INCIDENT_CHART,
    CONTINUITY_QUALITY_SUPPLY_INCIDENT_COMPARISON_CHART,
];

interface MvIncidentChartData {
    organizationId?: string;
    year?: string;
    xYear?: string;
    aerial?: number;
    underground?: number;
    cpi?: number;
    total?: number;
}

interface LvIncidentChartData {
    organizationId?: string;
    year?: string;
    xYear?: string;
    overheadBare?: number;
    twisted?: number;
    underground?: number;
    total?: number;
}

interface IncidentComparisonChartData {
    id?: number;
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-supply-continuity-incidentology',
    templateUrl: './supply-continuity-incidentology.component.html',
    styleUrls: ['./supply-continuity-incidentology.component.scss'],
})
export class SupplyContinuityIncidentologyComponent extends GenericComponent {
    public texts = CC_CONSTANTS.continuityQuality.tabs.incidentology.texts;

    public mvIncidentChartDataSignal: Signal<MvIncidentChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_MV_INCIDENT_CHART),
    ) as Signal<MvIncidentChartData[]>;
    public mvIncidentChartSeries: ApexAxisChartSeries = [];
    public mvIncidentYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.mvIncident.seriesTitle[0],
            min: 0,
            title: {
                text: this.texts.mvIncident.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.mvIncident.seriesTitle[0],
            min: 0,
        },
        {
            seriesName: this.texts.mvIncident.seriesTitle[0],
            min: 0,
        },
        {
            seriesName: this.texts.mvIncident.seriesTitle[3],
            min: 0,
            opposite: true,
            title: {
                text: this.texts.mvIncident.yaxisTitleRight,
            },
        },
    ];
    public mvIncidentChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
    };
    public mvIncidentChartGrid: ApexGrid = { padding: { left: 30 } };
    public mvIncidentChartError: boolean = true;

    public lvIncidentChartDataSignal: Signal<LvIncidentChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(CONTINUITY_QUALITY_SUPPLY_LV_INCIDENT_CHART),
    ) as Signal<LvIncidentChartData[]>;
    public lvIncidentChartSeries: ApexAxisChartSeries = [];

    public lvIncidentYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.lvIncident.seriesTitle[0],
            min: 0,
            title: {
                text: this.texts.lvIncident.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.lvIncident.seriesTitle[0],
            min: 0,
        },
        {
            seriesName: this.texts.lvIncident.seriesTitle[0],
            min: 0,
        },
        {
            seriesName: this.texts.lvIncident.seriesTitle[3],
            min: 0,
            opposite: true,
            title: {
                text: this.texts.lvIncident.yaxisTitleRight,
            },
        },
    ];
    public lvIncidentChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null, 3: null }),
    };
    public lvIncidentChartGrid: ApexGrid = { padding: { left: 30 } };
    public lvIncidentChartError: boolean = true;

    public incidentComparisonChartDataSignal: Signal<IncidentComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(
                CONTINUITY_QUALITY_SUPPLY_INCIDENT_COMPARISON_CHART,
            ),
        ) as Signal<IncidentComparisonChartData[]>;
    public incidentComparisonChartSeries: ApexAxisChartSeries = [];
    public incidentComparisonChartDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public incidentComparisonCustomTooltip: ApexTooltip['custom'];
    public incidentComparisonChartError: boolean = true;
    public incidentComparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public incidentComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            columnWidth: '40%',
            borderRadius: 1,
        },
    };
    public incidentComparisonYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
        },
    ];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const mvIncidentChartData = this.mvIncidentChartDataSignal();
            this.mvIncidentChartError = this.checkForError(mvIncidentChartData, [
                'xYear',
                'aerial',
                'underground',
                'cpi',
                'total',
            ]);
            if (!this.mvIncidentChartError) {
                this.mvIncidentChartSeries = [
                    {
                        name: this.texts.mvIncident.seriesTitle[0],
                        type: 'line',
                        data: mvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.aerial,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.mvIncident.seriesTitle[1],
                        type: 'line',
                        data: mvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.underground,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.mvIncident.seriesTitle[2],
                        type: 'line',
                        data: mvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.total,
                        })),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.mvIncident.seriesTitle[3],
                        type: 'line',
                        data: mvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.cpi,
                        })),
                        color: colorAec5Brown,
                    },
                ];
                this.mvIncidentChartDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const lvIncidentChartData = this.lvIncidentChartDataSignal();
            this.lvIncidentChartError = this.checkForError(lvIncidentChartData, [
                'xYear',
                'overheadBare',
                'twisted',
                'underground',
                'total',
            ]);
            if (!this.lvIncidentChartError) {
                this.lvIncidentChartSeries = [
                    {
                        name: this.texts.lvIncident.seriesTitle[0],
                        type: 'line',
                        data: lvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.twisted,
                        })),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.lvIncident.seriesTitle[1],
                        type: 'line',
                        data: lvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.underground,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lvIncident.seriesTitle[2],
                        type: 'line',
                        data: lvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.total,
                        })),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.lvIncident.seriesTitle[3],
                        type: 'line',
                        data: lvIncidentChartData.map((data) => ({
                            x: data.xYear?.toString(),
                            y: data.overheadBare,
                        })),
                        color: colorAec5Brown,
                    },
                ];
                this.lvIncidentChartDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            const incidentComparisonChartData = this.incidentComparisonChartDataSignal();
            this.incidentComparisonChartError = this.checkForError(incidentComparisonChartData, [
                'category',
                'min',
                'max',
                'mean',
            ]);
            if (!this.incidentComparisonChartError) {
                const customOrder = [3, 8, 7, 1, 5, 6, 4, 2];
                incidentComparisonChartData.sort(
                    (a: IncidentComparisonChartData, b: IncidentComparisonChartData) =>
                        customOrder.indexOf(a.id!) - customOrder.indexOf(b.id!),
                );

                this.incidentComparisonChartSeries = [
                    {
                        name: this.texts.incidentComparison.seriesTitle,
                        data: incidentComparisonChartData.map((data, index) => ({
                            x: this.texts.incidentComparison.categories[index],
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.incidentComparison.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 0,
                                    strokeHeight: 10,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },
                                {
                                    name: this.texts.incidentComparison.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 0,
                                    strokeHeight: 10,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.incidentComparison.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.incidentComparison.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }
        });
    }

    ngOnInit() {
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.incidentComparisonCustomTooltip = this.setChartCustomTooltip(
            this.texts.incidentComparison.precision,
        );
    }

    public setChartCustomTooltip(precision: number | null = null): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            const min = convertNumber(dataPoint.y[0], precision, false);
            const max = convertNumber(dataPoint.y[1], precision, false);

            const meanGoal = dataPoint.goals[0];
            const meanValue = convertNumber(meanGoal.value, precision, false);
            const meanName = meanGoal.name;
            const meanColor = meanGoal.strokeColor;

            const clientValueGoal = dataPoint.goals[1];
            const clientValue = convertNumber(clientValueGoal.value, precision, false);
            const clientValueName = clientValueGoal.name;
            const clientColor = clientValueGoal.strokeColor;

            return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} inc./100 km</span>
                        </div>
                            <div class="apexcharts-tooltip-goals-group">
                            </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} inc./100 km</span>
                        </div>
                            <div class="apexcharts-tooltip-goals-group">
                            </div>
                    </div>
                </div>
                    <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} inc./100 km</span>
                        </div>
                    </div>
                </div>
            `;
        };
    }
}
